import { EditorOptions, Editor as TiptapEditor } from '@tiptap/core'
import CharacterCount from '@tiptap/extension-character-count'
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Mention from '@tiptap/extension-mention'
import Placeholder from '@tiptap/extension-placeholder'
import TaskItem from '@tiptap/extension-task-item'
import TaskList from '@tiptap/extension-task-list'
import TextStyle from '@tiptap/extension-text-style'
import TypographyExtension from '@tiptap/extension-typography'
import { ReactNodeViewRenderer, ReactRenderer } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { message } from 'antd'
import axios from 'axios'
import { Plugin } from 'prosemirror-state'
import { handlePasteEvent } from 'rxjs-file-upload'
import tippy from 'tippy.js'

import { EditImage } from './editor-image.component'
import { MentionList, MentionUser } from './mention-list.component'

interface GetEditorProps extends Partial<EditorOptions> {
  users: MentionUser[]
  placeholder?: string
  textMaxCount?: number
}

export const getEditor = ({ users, placeholder, textMaxCount, ...editorProps }: GetEditorProps) => {
  placeholder ??= editorProps.editable ? '请填写' : '未填写'

  return new TiptapEditor({
    ...editorProps,
    extensions: [
      StarterKit,
      Highlight,
      TypographyExtension,
      Link,
      TaskList,
      TaskItem,
      Placeholder.configure({
        showOnlyWhenEditable: false,
        placeholder,
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        suggestion: {
          items(q) {
            return q ? users.filter((u) => u.email.split('@')[0].includes(q) || u.name.includes(q)).slice(0, 5) : []
          },
          render: () => {
            let reactRenderer: any
            let popup: any
            return {
              onStart: (props: any) => {
                reactRenderer = new ReactRenderer(MentionList, {
                  props,
                  editor: props.editor,
                })
                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: reactRenderer.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                })
              },
              onUpdate(props) {
                reactRenderer.updateProps(props)
                popup[0].setProps({
                  getReferenceClientRect: props.clientRect,
                })
              },
              onKeyDown(props) {
                if (props.event.key === 'Escape') {
                  popup[0].hide()
                  return true
                }
                return reactRenderer.ref?.onKeyDown(props)
              },
              onExit() {
                popup[0].destroy()
                reactRenderer.destroy()
              },
            }
          },
        },
      }),
      Image.extend({
        addNodeView() {
          return ReactNodeViewRenderer(EditImage)
        },
        addProseMirrorPlugins() {
          return [
            new Plugin({
              props: {
                handlePaste: (view, event) => {
                  let files: File[] = []
                  if (event.clipboardData) {
                    files = handlePasteEvent(event)
                  } else if (event['originalEvent']?.clipboardData) {
                    files = handlePasteEvent(event['originalEvent'])
                  }
                  if (files.length === 0) {
                    return false
                  }
                  message.info('Uploading...')
                  const file = files[0]
                  axios({
                    url: '/api/file/getOSSMeta',
                    params: {
                      name: file.name,
                      size: file.size,
                      type: file.type,
                    },
                  })
                    .then(({ data }) => {
                      const formData = Object.keys(data.oss).reduce(
                        (formData, key) => (formData.append(key, data.oss[key]), formData),
                        new FormData()
                      )
                      formData.append('file', file)
                      return axios.post(data.oss.uploadUrl, formData).then(() => data.url)
                    })
                    .then((src) => {
                      const { schema } = view.state
                      const node = schema.nodes.image.create({
                        src,
                      })
                      const transaction = view.state.tr.replaceSelectionWith(node)
                      view.dispatch(transaction)
                    })
                    .catch((err) => {
                      message.error('Upload Error ' + err.message)
                    })
                  return false
                },
              },
            }),
          ]
        },
      }),
      Color.configure({
        types: ['textStyle'],
      }),
      TextStyle,
      CharacterCount.configure({
        limit: textMaxCount,
      }),
    ],
    parseOptions: {
      preserveWhitespace: 'full',
    },
  })
}
